// useZoom.ts - Vue 3 Composable for zoom functionality with TypeScript and Touch Support
// This file has been produced with the help of AI
import { ref, reactive, onMounted, onUnmounted, computed, readonly, nextTick, type Ref, type ComputedRef } from 'vue'

// ===== TYPE DEFINITIONS =====

// Configuration interface
export interface UseZoomOptions {
  minScale?: number
  maxScale?: number
  zoomSpeed?: number
  enablePan?: boolean
  enableWheel?: boolean
  enableTouch?: boolean
  touchSensitivity?: number
  constrainToContainer?: boolean
  allowScaleSmallerThanContainer?: boolean
}

// Pointer tracking interface
interface PointerInfo {
  id: number
  x: number
  y: number
  startX: number
  startY: number
}

// Internal state interface
interface ZoomState {
  containerElement: HTMLElement | undefined
  zoomableElement: HTMLElement | undefined
  // Pointer/Touch state
  activePointers: Map<number, PointerInfo>
  initialDistance: number
  initialScale: number
  initialTranslateX: number
  initialTranslateY: number
  gestureCenter: { x: number; y: number } | null
  isGestureActive: boolean
}

// Boundary constraints interface
interface BoundaryConstraints {
  minX: number
  maxX: number
  minY: number
  maxY: number
}

// Return type interface - matches actual implementation
export interface UseZoomReturn {
  // Reactive state (readonly)
  scale: Readonly<Ref<number>>
  inverseScale: Readonly<Ref<number>>
  translateX: Readonly<Ref<number>>
  translateY: Readonly<Ref<number>>
  isDragging: Readonly<Ref<boolean>>
  isGestureActive: Readonly<Ref<boolean>>
  transform: ComputedRef<string>
  dynamicMinScale: Readonly<Ref<number>>

  // Methods
  bindToElements: (containerRef: HTMLElement | undefined, zoomableRef: HTMLElement | undefined) => void
  resetZoom: () => void
  zoomToScale: (targetScale: number, centerX?: number, centerY?: number) => void
  zoomIn: (factor?: number) => void
  zoomOut: (factor?: number) => void
  fitToContainer: () => void
  panTo: (x: number, y: number) => void
  cleanup: () => void
  updateConstraints: () => void
  injectPointer: (pointerId: number, clientX: number, clientY: number) => void

  // Coordinate methods
  containerToZoomable: (containerX: number, containerY: number) => { x: number; y: number }
  zoomableToContainer: (zoomableX: number, zoomableY: number) => { x: number; y: number }
  getEventCoordinates: (event: PointerEvent | MouseEvent | TouchEvent) => {
    container: { x: number; y: number }
    zoomable: { x: number; y: number }
  }

  // Convenience computed properties
  isZoomedIn: ComputedRef<boolean>
  isZoomedOut: ComputedRef<boolean>
  canZoomIn: ComputedRef<boolean>
  canZoomOut: ComputedRef<boolean>
  effectiveMinScale: Readonly<Ref<number>>
}

// Utility types for template refs
export type ZoomContainerRef = Ref<HTMLElement | undefined>
export type ZoomContentRef = Ref<HTMLElement | undefined>

// ===== IMPLEMENTATION =====

// Default configuration
const defaultConfig: Required<UseZoomOptions> = {
  minScale: 0.25,
  maxScale: 5,
  zoomSpeed: 0.1,
  enablePan: true,
  enableWheel: true,
  enableTouch: true,
  touchSensitivity: 1,
  constrainToContainer: true,
  allowScaleSmallerThanContainer: false,
}

export function useZoom(options: UseZoomOptions = {}): UseZoomReturn {
  // Configuration with defaults
  const config: Required<UseZoomOptions> = {
    ...defaultConfig,
    ...options,
  }

  // Reactive state
  const scale = ref<number>(1)
  const inverseScale = computed(() => 1 / scale.value)
  const translateX = ref<number>(0)
  const translateY = ref<number>(0)
  const isDragging = ref<boolean>(false)
  const isGestureActive = ref<boolean>(false)

  // Internal state
  const state = reactive<ZoomState>({
    containerElement: undefined,
    zoomableElement: undefined,
    activePointers: new Map(),
    initialDistance: 0,
    initialScale: 1,
    initialTranslateX: 0,
    initialTranslateY: 0,
    gestureCenter: null,
    isGestureActive: false,
  })

  // Computed transform string
  const transform = computed<string>(() => `translate(${translateX.value}px, ${translateY.value}px) scale(${scale.value})`)

  // Calculate the minimum scale needed to keep content at least as large as container
  const dynamicMinScale = computed<number>(() => {
    if (!state.containerElement || !state.zoomableElement || config.allowScaleSmallerThanContainer) {
      return config.minScale
    }

    const containerRect = state.containerElement.getBoundingClientRect()
    const zoomableRect = state.zoomableElement.getBoundingClientRect()

    if (containerRect.width === 0 || containerRect.height === 0 || zoomableRect.width === 0 || zoomableRect.height === 0) {
      return config.minScale
    }

    const scaleX = containerRect.width / (zoomableRect.width / scale.value)
    const scaleY = containerRect.height / (zoomableRect.height / scale.value)

    const calculatedMinScale = Math.max(scaleX, scaleY)
    return Math.max(calculatedMinScale, config.minScale)
  })

  // Get the effective minimum scale
  const effectiveMinScale = computed<number>(() => {
    return config.constrainToContainer ? dynamicMinScale.value : config.minScale
  })

  // Utility functions for touch/pointer handling
  const getDistance = (pointer1: PointerInfo, pointer2: PointerInfo): number => {
    const dx = pointer1.x - pointer2.x
    const dy = pointer1.y - pointer2.y
    return Math.sqrt(dx * dx + dy * dy)
  }

  const getCenter = (pointer1: PointerInfo, pointer2: PointerInfo): { x: number; y: number } => {
    return {
      x: (pointer1.x + pointer2.x) / 2,
      y: (pointer1.y + pointer2.y) / 2,
    }
  }

  const getPointerPosition = (e: PointerEvent): { x: number; y: number } => {
    if (!state.containerElement) return { x: 0, y: 0 }

    const rect = state.containerElement.getBoundingClientRect()
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    }
  }

  // Calculate boundary constraints for panning
  const getBoundaryConstraints = (): BoundaryConstraints | null => {
    if (!config.constrainToContainer || !state.containerElement || !state.zoomableElement) {
      return null
    }

    const containerRect = state.containerElement.getBoundingClientRect()
    const scaledWidth = state.zoomableElement.offsetWidth * scale.value
    const scaledHeight = state.zoomableElement.offsetHeight * scale.value

    return {
      minX: Math.min(0, containerRect.width - scaledWidth),
      maxX: Math.max(0, containerRect.width - scaledWidth),
      minY: Math.min(0, containerRect.height - scaledHeight),
      maxY: Math.max(0, containerRect.height - scaledHeight),
    }
  }

  // Constrain translation within boundaries
  const constrainTranslation = (x: number, y: number): { x: number; y: number } => {
    const constraints = getBoundaryConstraints()
    if (!constraints) {
      return { x, y }
    }

    return {
      x: Math.max(constraints.minX, Math.min(constraints.maxX, x)),
      y: Math.max(constraints.minY, Math.min(constraints.maxY, y)),
    }
  }

  // Update constraints and adjust current position if needed
  const updateConstraints = (): void => {
    if (!config.constrainToContainer) return

    const currentMinScale = effectiveMinScale.value
    if (scale.value < currentMinScale) {
      scale.value = currentMinScale
    }

    const constrained = constrainTranslation(translateX.value, translateY.value)
    translateX.value = constrained.x
    translateY.value = constrained.y
  }

  // State for classifying wheel input as trackpad scroll vs mouse wheel.
  // wheelRapidCount tracks consecutive rapid events (gap < 25 ms).
  // wheelSmallDeltaCount tracks a subset: rapid events with small deltas
  // (< 4 px), which indicate a gentle trackpad gesture starting from rest.
  let wheelLastTime = 0
  let wheelRapidCount = 0
  let wheelSmallDeltaCount = 0

  // Mouse wheel event handler
  const handleWheel = (e: WheelEvent): void => {
    if (!config.enableWheel || !state.containerElement) return

    // On macOS/Windows, trackpad pinch fires wheel events with ctrlKey=true.
    // This is the browser's convention for signalling a zoom gesture — the key
    // is not physically pressed. Allow these through even during active pointer
    // gestures so trackpad pinch is never blocked.
    const isPinchGesture = e.ctrlKey

    // On real touch devices (iPad, Android), the pointer path handles pinch and
    // pan — block all wheel events when a pointer gesture is active.
    // On macOS/Windows (navigator.maxTouchPoints === 0), the trackpad fires
    // wheel events for both pinch (ctrlKey) and scroll, so we let those through
    // even when pointer events have also fired for the finger contacts.
    const isTouchDevice = navigator.maxTouchPoints > 0
    if (state.isGestureActive && (!isPinchGesture || isTouchDevice)) return

    e.preventDefault()

    const rect = state.containerElement.getBoundingClientRect()
    const mouseX = e.clientX - rect.left
    const mouseY = e.clientY - rect.top

    // Classify input as trackpad two-finger scroll or mouse wheel using
    // three tiers, from fastest to most conservative:
    // Tier 1 (~0 ms): any horizontal delta — mouse wheels only produce deltaY.
    // Tier 2 (~50 ms): 3 consecutive small-delta rapid events — trackpad
    //   gestures start from rest with tiny deltas; mouse wheel clicks land at
    //   full system-configured delta (≥ 5 px) immediately.
    // Tier 3 (~115 ms): 7 consecutive rapid events — catches fast trackpad
    //   swipes that start with larger deltas and no horizontal component.
    // deltaMode !== 0 (Firefox) always means mouse wheel — reset counters.
    const now = performance.now()
    const gap = now - wheelLastTime
    wheelLastTime = now

    if (e.deltaMode === 0 && gap < 25) {
      wheelRapidCount++
      if (Math.abs(e.deltaY) < 30 && Math.abs(e.deltaX) < 30) {
        wheelSmallDeltaCount++
      }
    } else {
      wheelRapidCount = 0
      wheelSmallDeltaCount = 0
    }

    const isTrackpadScroll =
      !isPinchGesture &&
      e.deltaMode === 0 &&
      (Math.abs(e.deltaX) > 0.5 || // Tier 1: instant — horizontal delta
        wheelSmallDeltaCount >= 3 || // Tier 2: ~50 ms — gentle trackpad start
        wheelRapidCount >= 7) // Tier 3: ~115 ms — sustained rapid events

    if (isTrackpadScroll) {
      const newTranslateX = translateX.value - e.deltaX
      const newTranslateY = translateY.value - e.deltaY
      const constrained = constrainTranslation(newTranslateX, newTranslateY)
      translateX.value = constrained.x
      translateY.value = constrained.y
      return
    }

    // Use a proportional zoom factor for all cases. This means a tiny first
    // trackpad event misclassified as mouse wheel (before timing data exists)
    // causes an imperceptible <0.5% zoom rather than a jarring fixed 10% step.
    // Calibration: deltaMode=0 (Chrome/Safari) ≈ 100px per notch → 10% zoom;
    //              deltaMode=1 (Firefox) ≈ 3 lines per notch → 9% zoom.
    const deltaFactor = e.deltaMode === 0 ? 0.001 : 0.03
    const zoomFactor = isPinchGesture ? 1 - e.deltaY * config.zoomSpeed * 0.1 : 1 - e.deltaY * deltaFactor
    const newScale = scale.value * zoomFactor

    const currentMinScale = effectiveMinScale.value
    if (newScale < currentMinScale || newScale > config.maxScale) {
      return
    }

    const pointX = (mouseX - translateX.value) / scale.value
    const pointY = (mouseY - translateY.value) / scale.value

    scale.value = newScale
    const newTranslateX = mouseX - pointX * scale.value
    const newTranslateY = mouseY - pointY * scale.value

    const constrained = constrainTranslation(newTranslateX, newTranslateY)
    translateX.value = constrained.x
    translateY.value = constrained.y
  }

  // Pointer event handlers
  const handlePointerDown = (e: PointerEvent): void => {
    if (!config.enableTouch && !config.enablePan) return

    const position = getPointerPosition(e)
    const pointerInfo: PointerInfo = {
      id: e.pointerId,
      x: position.x,
      y: position.y,
      startX: position.x,
      startY: position.y,
    }

    state.activePointers.set(e.pointerId, pointerInfo)

    if (state.containerElement) {
      state.containerElement.setPointerCapture(e.pointerId)
    }

    // Handle single pointer (mouse-like behavior)
    if (state.activePointers.size === 1) {
      isDragging.value = true
      // Store current translation as starting point for drag calculation
      state.initialTranslateX = translateX.value
      state.initialTranslateY = translateY.value
      if (state.containerElement) {
        state.containerElement.style.cursor = 'grabbing'
      }
    }
    // Handle two pointers (pinch gesture)
    else if (state.activePointers.size === 2) {
      isDragging.value = false
      isGestureActive.value = true
      state.isGestureActive = true

      const pointers = Array.from(state.activePointers.values())
      state.initialDistance = getDistance(pointers[0], pointers[1])
      state.initialScale = scale.value
      state.initialTranslateX = translateX.value
      state.initialTranslateY = translateY.value
      state.gestureCenter = getCenter(pointers[0], pointers[1])

      if (state.containerElement) {
        state.containerElement.style.cursor = 'default'
      }
    }

    e.preventDefault()
  }

  const handlePointerMove = (e: PointerEvent): void => {
    const pointerInfo = state.activePointers.get(e.pointerId)
    if (!pointerInfo) return

    const position = getPointerPosition(e)
    pointerInfo.x = position.x
    pointerInfo.y = position.y

    // Single pointer drag
    if (state.activePointers.size === 1 && isDragging.value && config.enablePan) {
      const deltaX = position.x - pointerInfo.startX
      const deltaY = position.y - pointerInfo.startY

      const newTranslateX = state.initialTranslateX + deltaX
      const newTranslateY = state.initialTranslateY + deltaY

      const constrained = constrainTranslation(newTranslateX, newTranslateY)
      translateX.value = constrained.x
      translateY.value = constrained.y
    }
    // Two pointer pinch/pan
    else if (state.activePointers.size === 2 && state.isGestureActive && config.enableTouch) {
      const pointers = Array.from(state.activePointers.values())
      const currentDistance = getDistance(pointers[0], pointers[1])
      const currentCenter = getCenter(pointers[0], pointers[1])

      if (state.initialDistance > 0 && state.gestureCenter) {
        // Calculate pinch zoom
        const scaleChange = (currentDistance / state.initialDistance) * config.touchSensitivity
        const newScale = state.initialScale * scaleChange

        const currentMinScale = effectiveMinScale.value
        const clampedScale = Math.max(currentMinScale, Math.min(config.maxScale, newScale))

        // Calculate zoom center based on gesture center
        const pointX = (state.gestureCenter.x - state.initialTranslateX) / state.initialScale
        const pointY = (state.gestureCenter.y - state.initialTranslateY) / state.initialScale

        // Apply scale
        scale.value = clampedScale

        // Calculate base translation for zoom
        let newTranslateX = state.gestureCenter.x - pointX * clampedScale
        let newTranslateY = state.gestureCenter.y - pointY * clampedScale

        // Add pan offset from gesture center movement
        const centerDeltaX = currentCenter.x - state.gestureCenter.x
        const centerDeltaY = currentCenter.y - state.gestureCenter.y

        newTranslateX += centerDeltaX
        newTranslateY += centerDeltaY

        // Apply constraints
        const constrained = constrainTranslation(newTranslateX, newTranslateY)
        translateX.value = constrained.x
        translateY.value = constrained.y
      }
    }

    e.preventDefault()
  }

  const handlePointerUp = (e: PointerEvent): void => {
    state.activePointers.delete(e.pointerId)

    if (state.containerElement) {
      state.containerElement.releasePointerCapture(e.pointerId)
    }

    // Reset states when no more active pointers
    if (state.activePointers.size === 0) {
      isDragging.value = false
      isGestureActive.value = false
      state.isGestureActive = false
      state.gestureCenter = null

      if (state.containerElement) {
        state.containerElement.style.cursor = config.enablePan ? 'grab' : 'default'
      }
    }
    // Switch to single pointer mode if only one remains
    else if (state.activePointers.size === 1 && state.isGestureActive) {
      isGestureActive.value = false
      state.isGestureActive = false
      state.gestureCenter = null

      // Start single pointer drag from current position
      const remainingPointer = Array.from(state.activePointers.values())[0]
      remainingPointer.startX = remainingPointer.x
      remainingPointer.startY = remainingPointer.y
      state.initialTranslateX = translateX.value
      state.initialTranslateY = translateY.value

      if (config.enablePan) {
        isDragging.value = true
        if (state.containerElement) {
          state.containerElement.style.cursor = 'grabbing'
        }
      }
    }

    e.preventDefault()
  }

  const handlePointerCancel = (e: PointerEvent): void => {
    handlePointerUp(e)
  }

  // Fallback mouse event handlers for browsers without pointer events
  const handleMouseDown = (e: MouseEvent): void => {
    if (!config.enablePan || 'PointerEvent' in window) return

    // Convert mouse event to pointer-like event
    const position = { x: e.clientX, y: e.clientY }
    if (state.containerElement) {
      const rect = state.containerElement.getBoundingClientRect()
      position.x = e.clientX - rect.left
      position.y = e.clientY - rect.top
    }

    const pointerInfo: PointerInfo = {
      id: -1, // Use -1 for mouse
      x: position.x,
      y: position.y,
      startX: position.x,
      startY: position.y,
    }

    state.activePointers.set(-1, pointerInfo)
    isDragging.value = true
    state.initialTranslateX = translateX.value
    state.initialTranslateY = translateY.value

    if (state.containerElement) {
      state.containerElement.style.cursor = 'grabbing'
    }
  }

  const handleMouseMove = (e: MouseEvent): void => {
    if (!('PointerEvent' in window) && state.activePointers.has(-1) && isDragging.value) {
      const pointerInfo = state.activePointers.get(-1)
      if (!pointerInfo) return

      let position = { x: e.clientX, y: e.clientY }
      if (state.containerElement) {
        const rect = state.containerElement.getBoundingClientRect()
        position.x = e.clientX - rect.left
        position.y = e.clientY - rect.top
      }

      const deltaX = position.x - pointerInfo.startX
      const deltaY = position.y - pointerInfo.startY

      const newTranslateX = state.initialTranslateX + deltaX
      const newTranslateY = state.initialTranslateY + deltaY

      const constrained = constrainTranslation(newTranslateX, newTranslateY)
      translateX.value = constrained.x
      translateY.value = constrained.y
    }
  }

  const handleMouseUp = (): void => {
    if (!('PointerEvent' in window) && state.activePointers.has(-1)) {
      state.activePointers.delete(-1)
      isDragging.value = false

      if (state.containerElement) {
        state.containerElement.style.cursor = 'grab'
      }
    }
  }

  const handleContextMenu = (e: Event): void => {
    e.preventDefault()
  }

  // Public methods
  const resetZoom = (): void => {
    const resetScale = config.constrainToContainer ? effectiveMinScale.value : 1
    scale.value = resetScale

    if (state.containerElement && state.zoomableElement) {
      const containerRect = state.containerElement.getBoundingClientRect()
      const scaledWidth = state.zoomableElement.offsetWidth * scale.value
      const scaledHeight = state.zoomableElement.offsetHeight * scale.value

      translateX.value = (containerRect.width - scaledWidth) / 2
      translateY.value = (containerRect.height - scaledHeight) / 2
    } else {
      translateX.value = 0
      translateY.value = 0
    }

    const constrained = constrainTranslation(translateX.value, translateY.value)
    translateX.value = constrained.x
    translateY.value = constrained.y
  }

  const zoomToScale = (targetScale: number, centerX?: number, centerY?: number): void => {
    if (!state.containerElement) return

    const actualCenterX = centerX ?? state.containerElement.offsetWidth / 2
    const actualCenterY = centerY ?? state.containerElement.offsetHeight / 2

    const currentMinScale = effectiveMinScale.value
    const clampedScale = Math.max(currentMinScale, Math.min(config.maxScale, targetScale))

    const pointX = (actualCenterX - translateX.value) / scale.value
    const pointY = (actualCenterY - translateY.value) / scale.value

    scale.value = clampedScale
    const newTranslateX = actualCenterX - pointX * scale.value
    const newTranslateY = actualCenterY - pointY * scale.value

    const constrained = constrainTranslation(newTranslateX, newTranslateY)
    translateX.value = constrained.x
    translateY.value = constrained.y
  }

  const zoomIn = (factor = 1.2): void => {
    zoomToScale(scale.value * factor)
  }

  const zoomOut = (factor = 1.2): void => {
    zoomToScale(scale.value / factor)
  }

  const fitToContainer = (): void => {
    if (!state.containerElement || !state.zoomableElement) return

    const containerRect = state.containerElement.getBoundingClientRect()
    const zoomableRect = state.zoomableElement.getBoundingClientRect()

    const scaleX = containerRect.width / (zoomableRect.width / scale.value)
    const scaleY = containerRect.height / (zoomableRect.height / scale.value)
    const newScale = Math.min(scaleX, scaleY) * 0.95

    translateX.value = 0
    translateY.value = 0

    zoomToScale(newScale)
  }

  const panTo = (x: number, y: number): void => {
    const constrained = constrainTranslation(x, y)
    translateX.value = constrained.x
    translateY.value = constrained.y
  }

  // Convert container coordinates to zoomable content coordinates
  const containerToZoomable = (containerX: number, containerY: number): { x: number; y: number } => {
    // Account for translation and scale to get coordinates in the zoomable element's space
    const zoomableX = (containerX - translateX.value) / scale.value
    const zoomableY = (containerY - translateY.value) / scale.value

    return { x: zoomableX, y: zoomableY }
  }

  // Convert zoomable content coordinates to container coordinates
  const zoomableToContainer = (zoomableX: number, zoomableY: number): { x: number; y: number } => {
    // Apply scale and translation to get coordinates in the container's space
    const containerX = zoomableX * scale.value + translateX.value
    const containerY = zoomableY * scale.value + translateY.value

    return { x: containerX, y: containerY }
  }

  // Get coordinates from any pointer/mouse event on the container
  const getEventCoordinates = (
    event: PointerEvent | MouseEvent | TouchEvent,
  ): {
    container: { x: number; y: number }
    zoomable: { x: number; y: number }
  } => {
    if (!state.containerElement) {
      return {
        container: { x: 0, y: 0 },
        zoomable: { x: 0, y: 0 },
      }
    }

    const rect = state.containerElement.getBoundingClientRect()
    let clientX: number, clientY: number

    // Handle different event types
    if ('clientX' in event) {
      // PointerEvent or MouseEvent
      clientX = event.clientX
      clientY = event.clientY
    } else if ('touches' in event && event.touches.length > 0) {
      // TouchEvent - use first touch
      clientX = event.touches[0].clientX
      clientY = event.touches[0].clientY
    } else if ('changedTouches' in event && event.changedTouches.length > 0) {
      // TouchEvent on touchend - use first changed touch
      clientX = event.changedTouches[0].clientX
      clientY = event.changedTouches[0].clientY
    } else {
      return {
        container: { x: 0, y: 0 },
        zoomable: { x: 0, y: 0 },
      }
    }

    const containerCoords = {
      x: clientX - rect.left,
      y: clientY - rect.top,
    }

    const zoomableCoords = containerToZoomable(containerCoords.x, containerCoords.y)

    return {
      container: containerCoords,
      zoomable: zoomableCoords,
    }
  }

  // Inject an external pointer into the active-pointer tracking.
  // Used when a pointer was initially captured by a child element (e.g. a note)
  // and needs to be transferred to the container for a two-finger gesture.
  const injectPointer = (pointerId: number, clientX: number, clientY: number): void => {
    if (!state.containerElement) return
    const rect = state.containerElement.getBoundingClientRect()
    const x = clientX - rect.left
    const y = clientY - rect.top
    const pointerInfo: PointerInfo = { id: pointerId, x, y, startX: x, startY: y }
    state.activePointers.set(pointerId, pointerInfo)
    state.containerElement.setPointerCapture(pointerId)
  }

  // Setup function to bind to elements
  const bindToElements = (containerRef: HTMLElement | undefined, zoomableRef: HTMLElement | undefined): void => {
    state.containerElement = containerRef
    state.zoomableElement = zoomableRef

    if (!containerRef) return

    // Set initial cursor style
    if (config.enablePan) {
      containerRef.style.cursor = 'grab'
    }

    // Set touch-action to prevent default browser zoom/pan on touch devices
    if (config.enableTouch) {
      containerRef.style.touchAction = 'none'
    }

    // Add event listeners
    if (config.enableWheel) {
      containerRef.addEventListener('wheel', handleWheel, { passive: false })
    }

    // Use pointer events if available, otherwise fall back to mouse events
    if ('PointerEvent' in window) {
      if (config.enableTouch || config.enablePan) {
        containerRef.addEventListener('pointerdown', handlePointerDown)
        containerRef.addEventListener('pointermove', handlePointerMove)
        containerRef.addEventListener('pointerup', handlePointerUp)
        containerRef.addEventListener('pointercancel', handlePointerCancel)
      }
    } else {
      // Fallback for browsers without pointer events
      if (config.enablePan) {
        containerRef.addEventListener('mousedown', handleMouseDown)
      }
    }

    if (config.enablePan || config.enableTouch) {
      containerRef.addEventListener('contextmenu', handleContextMenu)
    }

    nextTick(() => {
      updateConstraints()
    })
  }

  // Cleanup function
  const cleanup = (): void => {
    if (state.containerElement) {
      state.containerElement.removeEventListener('wheel', handleWheel)
      state.containerElement.removeEventListener('pointerdown', handlePointerDown)
      state.containerElement.removeEventListener('pointermove', handlePointerMove)
      state.containerElement.removeEventListener('pointerup', handlePointerUp)
      state.containerElement.removeEventListener('pointercancel', handlePointerCancel)
      state.containerElement.removeEventListener('mousedown', handleMouseDown)
      state.containerElement.removeEventListener('contextmenu', handleContextMenu)
    }

    // Clear active pointers
    state.activePointers.clear()
    isDragging.value = false
    isGestureActive.value = false
    state.isGestureActive = false
  }

  // Auto-setup global mouse events for fallback
  onMounted(() => {
    if (!('PointerEvent' in window) && config.enablePan) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    }
  })

  onUnmounted(() => {
    cleanup()
    if (!('PointerEvent' in window) && config.enablePan) {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  })

  // Convenience computed properties
  const isZoomedIn = computed<boolean>(() => scale.value > effectiveMinScale.value)
  const isZoomedOut = computed<boolean>(() => scale.value <= effectiveMinScale.value)
  const canZoomIn = computed<boolean>(() => scale.value < config.maxScale)
  const canZoomOut = computed<boolean>(() => scale.value > effectiveMinScale.value)

  // Return reactive state and methods
  return {
    // Reactive state (readonly to prevent external mutation)
    scale: readonly(scale),
    inverseScale: readonly(inverseScale),
    translateX: readonly(translateX),
    translateY: readonly(translateY),
    isDragging: readonly(isDragging),
    isGestureActive: readonly(isGestureActive),
    transform,
    dynamicMinScale: readonly(dynamicMinScale),

    // Methods
    bindToElements,
    resetZoom,
    zoomToScale,
    zoomIn,
    zoomOut,
    fitToContainer,
    panTo,
    cleanup,
    updateConstraints,
    injectPointer,

    // Coordinate conversion utilities
    containerToZoomable,
    zoomableToContainer,
    getEventCoordinates,

    // Convenience computed properties
    isZoomedIn,
    isZoomedOut,
    canZoomIn,
    canZoomOut,
    effectiveMinScale: readonly(effectiveMinScale),
  }
}
